import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  Users, Search, Eye, Edit, Send, RefreshCw, Download,
  User, MapPin, Briefcase, GraduationCap, CreditCard, UserCheck,
  CheckCircle, Clock, AlertCircle, Building, Calendar as CalendarIcon, ClipboardCheck,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDDMMYYYY } from '@/utils/textFormatting';
import axios from 'axios';
import { format } from 'date-fns';

export default function CandidateDraftsTab({ onEditDraft }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkSubmitDialog, setShowBulkSubmitDialog] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Progress badge filter state
  const [progressFilter, setProgressFilter] = useState('all'); // 'all', 'almost_complete', 'in_progress', 'just_started'

  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showDateFrom, setShowDateFrom] = useState(false);
  const [showDateTo, setShowDateTo] = useState(false);

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/candidate-drafts');
      setDrafts(response.data);
    } catch (error) {
      toast.error('Failed to load candidate drafts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDraft = (draft) => {
    if (onEditDraft) {
      onEditDraft(draft);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get('/candidate-drafts/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Draft_Cases_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Bulk selection for submitting drafts to Pending QC
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const eligibleIds = filteredDrafts
      .filter(d => d.case_id && d.case_status === 'initiated')
      .map(d => d.case_id);
    if (selectedIds.size === eligibleIds.length && eligibleIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleIds));
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.size === 0) return;
    setBulkSubmitting(true);
    try {
      const response = await axios.post('/qc/bulk-submit', {
        case_ids: Array.from(selectedIds)
      });
      toast.success(response.data.message || `${response.data.submitted} cases submitted to Pending QC`);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(`${err.case_id}: ${err.error}`));
      }
      setSelectedIds(new Set());
      setShowBulkSubmitDialog(false);
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk submit cases');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // Send bulk reminders
  const handleBulkReminder = async () => {
    // Get IDs of invited candidates with valid emails
    const eligibleDrafts = filteredDrafts.filter(d => {
      if (!d.id || !(d.invited || d.invitation_sent || d.invitation_sent_at)) return false;
      const email = (d.email || '').trim().toLowerCase();
      if (!email || !email.includes('@')) return false;
      if (email.startsWith('na@') || email.endsWith('@test.com') || email.startsWith('test@')) return false;
      return true;
    });
    if (eligibleDrafts.length === 0) {
      toast.warning('No eligible candidates to send reminders to. Candidates must have received an invitation first.');
      return;
    }
    setSendingReminders(true);
    try {
      const response = await axios.post('/candidate-drafts/bulk-reminder', {
        employee_ids: eligibleDrafts.map(d => d.id)
      });
      const { sent, skipped, failed } = response.data;
      if (sent > 0) {
        toast.success(`Reminders sent to ${sent} candidate(s)`);
      }
      if (skipped > 0) {
        toast.warning(`${skipped} skipped (not invited or no email)`);
      }
      if (failed > 0) {
        toast.error(`${failed} failed to send`);
      }
      fetchDrafts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // Date filter helpers
  const getDateRange = (preset) => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
      case 'today':
        return { from: startOfDay, to: new Date(startOfDay.getTime() + 86400000) };
      case 'this_week': {
        const day = startOfDay.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;
        const monday = new Date(startOfDay);
        monday.setDate(monday.getDate() + mondayOffset);
        return { from: monday, to: new Date(startOfDay.getTime() + 86400000) };
      }
      case 'this_month':
        return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(startOfDay.getTime() + 86400000) };
      default:
        return null;
    }
  };

  // Filter by search + date
  const filteredDrafts = useMemo(() => {
    let result = drafts;

    // Progress badge filter
    if (progressFilter !== 'all') {
      result = result.filter(draft => {
        const pct = draft.progress?.percentage || 0;
        if (progressFilter === 'almost_complete') return pct >= 80;
        if (progressFilter === 'in_progress') return pct >= 20 && pct < 80;
        if (progressFilter === 'just_started') return pct < 20;
        return true;
      });
    }

    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(draft =>
        draft.name?.toLowerCase().includes(search) ||
        draft.email?.toLowerCase().includes(search) ||
        draft.client_name?.toLowerCase().includes(search) ||
        draft.case_id?.toLowerCase().includes(search)
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      let from, to;
      if (dateFilter === 'range') {
        from = dateFrom;
        to = dateTo ? new Date(dateTo.getTime() + 86400000) : null;
      } else {
        const range = getDateRange(dateFilter);
        if (range) { from = range.from; to = range.to; }
      }
      if (from || to) {
        result = result.filter(draft => {
          const rawDate = draft.invitation_sent_at || draft.created_at;
          if (!rawDate) return false;
          const d = new Date(rawDate);
          if (from && d < from) return false;
          if (to && d >= to) return false;
          return true;
        });
      }
    }

    return result;
  }, [drafts, searchTerm, progressFilter, dateFilter, dateFrom, dateTo]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredDrafts.length / pageSize));
  const paginatedDrafts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDrafts.slice(start, start + pageSize);
  }, [filteredDrafts, currentPage, pageSize]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, pageSize, progressFilter, dateFilter, dateFrom, dateTo]);

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getProgressBadge = (percentage) => {
    if (percentage >= 80) return { variant: 'default', className: 'bg-emerald-500', label: 'Almost Complete' };
    if (percentage >= 50) return { variant: 'secondary', className: 'bg-amber-500 text-white', label: 'In Progress' };
    if (percentage > 0) return { variant: 'secondary', className: 'bg-orange-500 text-white', label: 'Started' };
    return { variant: 'outline', className: '', label: 'Not Started' };
  };

  const invitedCount = filteredDrafts.filter(d => {
    if (!(d.invited || d.invitation_sent || d.invitation_sent_at)) return false;
    const email = (d.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return false;
    if (email.startsWith('na@') || email.endsWith('@test.com') || email.startsWith('test@')) return false;
    return true;
  }).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid="candidate-drafts-title">Candidate Drafts</h2>
          <p className="text-slate-600">Employees who started data entry but haven't submitted yet.</p>
        </div>
        <div className="flex items-center gap-2">
          {filteredDrafts.filter(d => d.case_id && d.case_status === 'initiated').length > 0 && (
            <Button onClick={toggleSelectAll} variant="outline" size="sm" data-testid="draft-select-all">
              {selectedIds.size === filteredDrafts.filter(d => d.case_id && d.case_status === 'initiated').length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowBulkSubmitDialog(true)}
              data-testid="draft-bulk-submit-btn"
            >
              <ClipboardCheck className="w-4 h-4 mr-1" />
              Bulk Submit ({selectedIds.size})
            </Button>
          )}
          <Button onClick={fetchDrafts} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" disabled={exporting} data-testid="draft-export-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </div>
      </div>

      {/* Bulk Submit Confirmation Dialog */}
      <Dialog open={showBulkSubmitDialog} onOpenChange={setShowBulkSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Drafts to Pending QC</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-2">
              You are about to submit <span className="font-bold text-slate-900">{selectedIds.size}</span> draft case(s) for QC review.
            </p>
            <p className="text-sm text-slate-500 mb-4">
              This will move the selected drafts to <strong>Pending QC</strong> status.
            </p>
            <div className="max-h-40 overflow-y-auto border rounded p-2">
              {Array.from(selectedIds).map(caseId => {
                const d = drafts.find(x => x.case_id === caseId);
                return (
                  <div key={caseId} className="text-sm py-1 border-b last:border-0 flex justify-between">
                    <span className="text-slate-700">{d?.name || 'Unknown'}</span>
                    <span className="font-mono text-slate-400 text-xs">{caseId}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSubmitDialog(false)} data-testid="draft-bulk-submit-cancel">Cancel</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleBulkSubmit}
              disabled={bulkSubmitting}
              data-testid="draft-bulk-submit-confirm"
            >
              {bulkSubmitting ? 'Submitting...' : `Submit ${selectedIds.size} to QC`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards - Clickable to filter */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${progressFilter === 'all' ? 'ring-2 ring-slate-400' : ''}`}
          onClick={() => setProgressFilter('all')}
          data-testid="draft-filter-all"
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Drafts</p>
                <p className="text-2xl font-bold text-slate-900">{drafts.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-emerald-200 bg-emerald-50 cursor-pointer transition-all hover:shadow-md ${progressFilter === 'almost_complete' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setProgressFilter(progressFilter === 'almost_complete' ? 'all' : 'almost_complete')}
          data-testid="draft-filter-almost-complete"
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Almost Complete</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {drafts.filter(d => d.progress?.percentage >= 80).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-amber-200 bg-amber-50 cursor-pointer transition-all hover:shadow-md ${progressFilter === 'in_progress' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setProgressFilter(progressFilter === 'in_progress' ? 'all' : 'in_progress')}
          data-testid="draft-filter-in-progress"
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">In Progress</p>
                <p className="text-2xl font-bold text-amber-700">
                  {drafts.filter(d => d.progress?.percentage >= 20 && d.progress?.percentage < 80).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-rose-200 bg-rose-50 cursor-pointer transition-all hover:shadow-md ${progressFilter === 'just_started' ? 'ring-2 ring-rose-500' : ''}`}
          onClick={() => setProgressFilter(progressFilter === 'just_started' ? 'all' : 'just_started')}
          data-testid="draft-filter-just-started"
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-600">Just Started</p>
                <p className="text-2xl font-bold text-rose-700">
                  {drafts.filter(d => d.progress?.percentage < 20).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-rose-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Bulk Reminder + Date Filter */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, client, or case ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="draft-search-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleBulkReminder}
          disabled={sendingReminders || invitedCount === 0}
          className="border-amber-300 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
          data-testid="draft-bulk-reminder-btn"
        >
          <Bell className="w-4 h-4 mr-1" />
          {sendingReminders ? 'Sending...' : `Send Reminder (${invitedCount})`}
        </Button>
      </div>

      {/* Date Filter Row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500 font-medium">Request Date:</span>
        {['all', 'today', 'this_week', 'this_month', 'range'].map((val) => {
          const labels = { all: 'All', today: 'Today', this_week: 'This Week', this_month: 'This Month', range: 'Date Range' };
          return (
            <Button
              key={val}
              variant={dateFilter === val ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setDateFilter(val); if (val !== 'range') { setDateFrom(null); setDateTo(null); } }}
              data-testid={`draft-date-filter-${val}`}
            >
              {labels[val]}
            </Button>
          );
        })}
        {dateFilter === 'range' && (
          <div className="flex items-center gap-2 ml-2">
            <Popover open={showDateFrom} onOpenChange={setShowDateFrom}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[130px] justify-start" data-testid="draft-date-from">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'From'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setShowDateFrom(false); }} />
              </PopoverContent>
            </Popover>
            <span className="text-slate-400">-</span>
            <Popover open={showDateTo} onOpenChange={setShowDateTo}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[130px] justify-start" data-testid="draft-date-to">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'To'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setShowDateTo(false); }} />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Pagination Controls (Above List) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Show</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-[70px] h-8" data-testid="draft-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span>of {filteredDrafts.length} entries</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} data-testid="draft-page-first">
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="draft-page-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="draft-page-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} data-testid="draft-page-last">
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Drafts List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading candidate drafts...</div>
      ) : paginatedDrafts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm || dateFilter !== 'all' ? 'No drafts match your filters' : 'No candidate drafts found'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {paginatedDrafts.map((draft) => {
            const progressBadge = getProgressBadge(draft.progress?.percentage || 0);
            return (
              <Card key={draft.id} className={`border-slate-200 hover:shadow-md transition-shadow ${draft.case_id && selectedIds.has(draft.case_id) ? 'ring-2 ring-blue-400 border-blue-300' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Left: Candidate Info */}
                    <div className="flex items-center gap-4 flex-1">
                      {draft.case_id && draft.case_status === 'initiated' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(draft.case_id)}
                          onChange={() => toggleSelect(draft.case_id)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`draft-checkbox-${draft.case_id}`}
                        />
                      )}
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{draft.name}</h3>
                          <Badge {...progressBadge}>{progressBadge.label}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 truncate">{draft.email}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                          {draft.client_name && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {draft.client_name}
                            </span>
                          )}
                          {draft.case_id && (
                            <span className="flex items-center gap-1">
                              Case: {draft.case_id}
                            </span>
                          )}
                          {(draft.invitation_sent_at || draft.created_at) && (
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              Requested: {formatDateDDMMYYYY(draft.invitation_sent_at || draft.created_at)}
                            </span>
                          )}
                          {draft.last_reminder_sent_at && (
                            <span className="flex items-center gap-1 text-amber-500">
                              <Bell className="w-3 h-3" />
                              Last Reminder: {formatDateDDMMYYYY(draft.last_reminder_sent_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Middle: Progress */}
                    <div className="w-48 px-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">Progress</span>
                        <span className={`text-sm font-semibold ${getProgressColor(draft.progress?.percentage || 0)}`}>
                          {draft.progress?.percentage || 0}%
                        </span>
                      </div>
                      <Progress value={draft.progress?.percentage || 0} className="h-2" />
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                        {draft.progress?.sections && (
                          <>
                            {draft.progress.sections.basic && <span className="text-emerald-500">●</span>}
                            {draft.progress.sections.address && <span className="text-emerald-500">●</span>}
                            {draft.progress.sections.employment && <span className="text-emerald-500">●</span>}
                            {draft.progress.sections.education && <span className="text-emerald-500">●</span>}
                            {draft.progress.sections.id_details && <span className="text-emerald-500">●</span>}
                            {draft.progress.sections.references && <span className="text-emerald-500">●</span>}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDraft(draft)}
                        data-testid={`view-draft-${draft.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View & Edit
                      </Button>
                    </div>
                  </div>

                  {/* Section Progress Icons */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.basic ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <User className="w-3 h-3" />
                      Basic
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.address ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <MapPin className="w-3 h-3" />
                      Address
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.employment ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <Briefcase className="w-3 h-3" />
                      Employment
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.education ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <GraduationCap className="w-3 h-3" />
                      Education
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.id_details ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <CreditCard className="w-3 h-3" />
                      ID Details
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.references ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <UserCheck className="w-3 h-3" />
                      References
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
